/**
 * @typedef AppStore
 * @property {LoginInfo} loginInfo
 // * @property {UserInfo} userInfo
 * @property {OrderInfo} orderInfo
 * @property {MyGeolocation} geoLocation
 * @property {CarModelInfo} carModelInfo
 * @property {StoreInfo} storeInfo
 * @property {TestDriveDateTime} testDriveDateTime
 * @property {Object} testDriveFormData
 * @property {Object} preOrderFormData
 * @property {Array} formDataArray
 * @property {Object} storeListCache
 *********
 * @property {String} nickName
 * @property {Boolean} hasLoggedIn
 * @property {Boolean} hasGeoLocation
 * ********
 * @property {UpdateLoginInfo} updateLoginInfo
 * @property {SetLoginInfo} setLoginInfo
 * @property {SetUserInfo} setUserInfo
 * @property {UpdateUserInfo} updateUserInfo
 * @property {SetOrderInfo} setOrderInfo
 * @property {UpdateOrderInfo} updateOrderInfo
 * @property {SetGeoLocation} setGeoLocation
 * @property {UpdateGeoLocation} updateGeoLocation
 * @property {SetCarModelInfo} setCarModelInfo
 * @property {UpdateCarModelInfo} updateCarModelInfo
 * @property {SetStoreInfo} setStoreInfo
 * @property {UpdateStoreInfo} updateStoreInfo
 * @property {SetTestDriveDateTime} setTestDriveDateTime
 * @property {UpdateTestDriveDateTime} updateTestDriveDateTime
 * @property {SetTestDriveFormData} setTestDriveFormData
 * @property {UpdateTestDriveFormData} updateTestDriveFormData
 * @property {SetPreOrderFormData} setPreOrderFormData
 * @property {UpdatePreOrderFormData} updatePreOrderFormData
 * @property {SetFormDataArray} setFormDataArray
 * @property {UpdateFormDataArray} updateFormDataArray
 * @property {AddFormData} addFormData
 * @property {RemoveFormData} removeFormData
 * @property {SetStoreListCache} setStoreListCache
 * @property {GetStoreListCache} getStoreListCache
 * @property {ClearStoreListCache} clearStoreListCache
 * */
/**
 * @typedef LoginInfo
 * @property {String} token
 * @property {String} firstName
 * @property {String} lastName
 * @property {String} nickName
 * */
/**
 * @typedef UserInfo
 * */
/**
 * @typedef OrderInfo
 * @property {Array} orderList
 * */
/**
 * @typedef MyGeolocation
 * @property {String} longitude
 * @property {String} latitude
 * @property {Boolean} authorized
 * */
/**
 * @typedef CarModelInfo
 * @property {String} carModel
 * @property {String} carModelRadio
 * @property {Number} currentIndex
 * @property {Object} selectedItem
 * */
/**
 * @typedef StoreInfo
 * @property {String} storeId
 * @property {String} storeName
 * @property {String} storeAddress
 * @property {String} latitude
 * @property {String} longitude
 * @property {Number} currentIndex
 * @property {Object} selectedItem
 * */
/**
 * @typedef TestDriveDateTime
 * @property {String} date
 * @property {String} timeId
 * @property {String} timeText
 * */
import { defineStore } from 'pinia';
import { computed, reactive, toRefs } from 'vue';
import { webStorage } from '@/utils/web-storage';
import {
  S_LOGIN_INFO,
  S_USER_INFO,
  S_ORDER_LIST,
  S_MALL_ORDER_LIST,
  S_GEO_LOCATION,
  S_LANG,
  S_SITE_INFO,
  S_CAR_MODEL_INFO,
  S_STORE_INFO,
  S_TESTDRIVE_DATE_TIME,
  S_TESTDRIVE_FORM_DATA,
  S_PREORDER_FORM_DATA,
  S_STORE_LIST_CACHE,
  S_TESTDRIVE_STORE_LIST_CACHE,
  S_FORM_DATA_ARRAY,
} from '@/utils/web-storage-keys';
import { canUseDOM } from '@/utils/dom-utils';
import { merge } from 'lodash';
// import { qtUtils } from '@/utils/ali-tracker-utils';
import { isNoE } from '@/utils/obj-utils';
let useAppStore = () => Object.create(null);
if (canUseDOM()) {
  /**@type LoginInfo*/
  const loginInfo = webStorage.get(S_LOGIN_INFO);
  /**@type OrderInfo*/
  const orderInfo = webStorage.get(S_ORDER_LIST);
  const mallOrderInfo = webStorage.get(S_MALL_ORDER_LIST);
  /**@type MyGeolocation*/
  const geoLocation = webStorage.get(S_GEO_LOCATION);
  const site = webStorage.get(S_SITE_INFO);
  const lang = webStorage.get(S_LANG);
  /**@type CarModelInfo*/
  const carModelInfo = webStorage.get(S_CAR_MODEL_INFO);
  /**@type StoreInfo*/
  const storeInfo = webStorage.get(S_STORE_INFO);
  /**@type TestDriveDateTime*/
  const testDriveDateTime = webStorage.get(S_TESTDRIVE_DATE_TIME);
  /**@type Object*/
  const testDriveFormData = webStorage.get(S_TESTDRIVE_FORM_DATA);
  /**@type Object*/
  const preOrderFormData = webStorage.get(S_PREORDER_FORM_DATA);
  /**@type Array*/
  const formDataArray = webStorage.get(S_FORM_DATA_ARRAY) || [];
  /**@type Object*/
  const storeListCache = webStorage.get(S_STORE_LIST_CACHE);
  /**@type Object*/
  const testDriveStoreListCache = webStorage.get(S_TESTDRIVE_STORE_LIST_CACHE);
  /**
   * @return AppStore
   */
  useAppStore = defineStore('app', () => {
    const state = reactive({
      loginInfo,
      orderInfo,
      mallOrderInfo,
      geoLocation,
      site,
      lang,
      carModelInfo,
      storeInfo,
      testDriveDateTime,
      testDriveFormData,
      preOrderFormData,
      formDataArray,
      storeListCache,
      testDriveStoreListCache,
      showCookieSettingModal: false,
      cookieSettingModalClosed: false,
      showTitle: true,
    });
    const computes = {
      nickName: computed(() => state?.loginInfo?.nickName),
      hasLoggedIn: computed(() => !!state?.loginInfo?.levcId),
      hasGeoLocation: computed(
        () => !isNoE(state?.geoLocation?.longitude) && !isNoE(state?.geoLocation?.latitude)
      ),
    };
    const methods = {
      /**
       * @callback SetLoginInfo
       * @param {LoginInfo} payload
       * */
      setLoginInfo(payload) {
        webStorage.set(S_LOGIN_INFO, payload);
        state.loginInfo = payload;
      },
      /**
       * @callback UpdateLoginInfo
       * @param {LoginInfo} payload
       * */
      updateLoginInfo(payload) {
        const oldInfo = webStorage.get(S_LOGIN_INFO);
        const newInfo = Object.create(null);
        merge(newInfo, oldInfo, payload);
        webStorage.set(S_LOGIN_INFO, newInfo);
        state.loginInfo = newInfo;
      },
      /**
       * @callback SetUserInfo
       * @param {UserInfo} payload
       */
      setUserInfo(payload) {
        webStorage.set(S_USER_INFO, payload);
        state.userInfo = payload;
      },
      /**
       * @callback UpdateUserInfo
       * @param {UserInfo} payload
       */
      UpdateUserInfo(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.userInfo, payload);
        webStorage.set(S_USER_INFO, newInfo);
        state.userInfo = newInfo;
      },
      /**
       * @callback SetOrderInfo
       * @param {OrderInfo} payload
       */
      setOrderInfo(payload) {
        webStorage.set(S_ORDER_LIST, payload);
        state.orderInfo = payload;
      },
      /**
       * @callback UpdateOrderInfo
       * @param {OrderInfo} payload
       */
      UpdateOrderInfo(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.orderInfo, payload);
        webStorage.set(S_ORDER_LIST, newInfo);
        state.orderInfo = newInfo;
      },
      setMallOrderInfo(payload) {
        webStorage.set(S_MALL_ORDER_LIST, payload);
        state.mallOrderInfo = payload;
      },
      /**
       * @callback UpdateOrderInfo
       * @param {OrderInfo} payload
       */
      UpdateMallOrderInfo(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.mallOrderInfo, payload);
        webStorage.set(S_MALL_ORDER_LIST, newInfo);
        state.mallOrderInfo = newInfo;
      },
      /**
       * @callback SetGeoLocation
       * @param {MyGeolocation} payload
       */
      setGeoLocation(payload) {
        webStorage.set(S_GEO_LOCATION, payload);
        state.geoLocation = payload;
      },
      /**
       * @callback UpdateGeoLocation
       * @param {MyGeolocation} payload
       */
      updateGeoLocation(payload) {
        const newPayload = Object.create(null);
        merge(newPayload, state.geoLocation, payload);
        webStorage.set(S_MALL_ORDER_LIST, newPayload);
        state.mallOrderInfo = newPayload;
      },
      /**
       * @callback SetLoginInfo
       * @param {LoginInfo} payload
       * */
      setSite(payload) {
        webStorage.set(S_SITE_INFO, payload);
        state.site = payload;
        // qtUtils.setMetaInfo('_user_id', payload?.levcId ?? null);
      },
      setLang(payload) {
        webStorage.set(S_LANG, payload);
        state.lang = payload;
      },
      setShowCookieSettingModal(payload) {
        state.showCookieSettingModal = payload;
      },
      setCookieSettingModalClosed(payload) {
        state.cookieSettingModalClosed = payload;
      },
      updateShowTitle(payload) {
        state.showTitle = payload;
      },
      /**
       * @callback SetCarModelInfo
       * @param {CarModelInfo} payload
       */
      setCarModelInfo(payload) {
        webStorage.set(S_CAR_MODEL_INFO, payload);
        state.carModelInfo = payload;
      },
      /**
       * @callback UpdateCarModelInfo
       * @param {CarModelInfo} payload
       */
      updateCarModelInfo(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.carModelInfo, payload);
        webStorage.set(S_CAR_MODEL_INFO, newInfo);
        state.carModelInfo = newInfo;
      },
      /**
       * @callback SetStoreInfo
       * @param {StoreInfo} payload
       */
      setStoreInfo(payload) {
        webStorage.set(S_STORE_INFO, payload);
        state.storeInfo = payload;
      },
      /**
       * @callback UpdateStoreInfo
       * @param {StoreInfo} payload
       */
      updateStoreInfo(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.storeInfo, payload);
        webStorage.set(S_STORE_INFO, newInfo);
        state.storeInfo = newInfo;
      },
      /**
       * @callback SetTestDriveDateTime
       * @param {TestDriveDateTime} payload
       */
      setTestDriveDateTime(payload) {
        webStorage.set(S_TESTDRIVE_DATE_TIME, payload);
        state.testDriveDateTime = payload;
      },
      /**
       * @callback UpdateTestDriveDateTime
       * @param {TestDriveDateTime} payload
       */
      updateTestDriveDateTime(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.testDriveDateTime, payload);
        webStorage.set(S_TESTDRIVE_DATE_TIME, newInfo);
        state.testDriveDateTime = newInfo;
      },
      /**
       * @callback SetTestDriveFormData
       * @param {Object} payload
       */
      setTestDriveFormData(payload) {
        webStorage.set(S_TESTDRIVE_FORM_DATA, payload);
        state.testDriveFormData = payload;
      },
      /**
       * @callback UpdateTestDriveFormData
       * @param {Object} payload
       */
      updateTestDriveFormData(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.testDriveFormData, payload);
        webStorage.set(S_TESTDRIVE_FORM_DATA, newInfo);
        state.testDriveFormData = newInfo;
      },
      /**
       * @callback SetPreOrderFormData
       * @param {Object} payload
       */
      setPreOrderFormData(payload) {
        webStorage.set(S_PREORDER_FORM_DATA, payload);
        state.preOrderFormData = payload;
      },
      /**
       * @callback UpdatePreOrderFormData
       * @param {Object} payload
       */
      updatePreOrderFormData(payload) {
        const newInfo = Object.create(null);
        merge(newInfo, state.preOrderFormData, payload);
        webStorage.set(S_PREORDER_FORM_DATA, newInfo);
        state.preOrderFormData = newInfo;
      },
      /**
       * @callback SetFormDataArray
       * @param {Array} payload - 表单数据数组
       */
      setFormDataArray(payload) {
        webStorage.set(S_FORM_DATA_ARRAY, payload);
        state.formDataArray = payload;
      },
      /**
       * @callback UpdateFormDataArray
       * @param {Array} payload - 表单数据数组
       */
      updateFormDataArray(payload) {
        const newArray = [...payload];
        webStorage.set(S_FORM_DATA_ARRAY, newArray);
        state.formDataArray = newArray;
      },
      /**
       * @callback AddFormData
       * @param {String} formType - 表单类型 (如: 'contactUs', 'testDrive')
       * @param {Object} formData - 表单数据
       */
      addFormData(formType, formData) {
        const newArray = [...state.formDataArray];
        const existingIndex = newArray.findIndex((item) => item[formType] !== undefined);

        if (existingIndex !== -1) {
          // 如果已存在该类型的表单数据，则更新
          newArray[existingIndex] = { [formType]: formData };
        } else {
          // 如果不存在，则添加新的表单数据
          newArray.push({ [formType]: formData });
        }

        webStorage.set(S_FORM_DATA_ARRAY, newArray);
        state.formDataArray = newArray;
      },
      /**
       * @callback RemoveFormData
       * @param {String} formType - 表单类型 (如: 'contactUs', 'testDrive')
       */
      removeFormData(formType) {
        const newArray = state.formDataArray.filter((item) => item[formType] === undefined);
        webStorage.set(S_FORM_DATA_ARRAY, newArray);
        state.formDataArray = newArray;
      },
      /**
       * @callback SetStoreListCache
       * @param {Object} payload - 包含门店列表数据和缓存时间戳
       */
      setStoreListCache(payload) {
        webStorage.set(S_STORE_LIST_CACHE, payload);
        state.storeListCache = payload;
      },
      /**
       * @callback GetStoreListCache
       * @returns {Object|null} 返回缓存的门店列表数据
       */
      getStoreListCache() {
        return state.storeListCache;
      },
      /**
       * @callback ClearStoreListCache
       * 清除门店列表缓存
       */
      clearStoreListCache() {
        webStorage.remove(S_STORE_LIST_CACHE);
        state.storeListCache = null;
      },
      /**
       * @callback SetTestDriveStoreListCache
       * @param {Object} payload - 包含试驾门店列表数据和缓存时间戳
       */
      setTestDriveStoreListCache(payload) {
        webStorage.set(S_TESTDRIVE_STORE_LIST_CACHE, payload);
        state.testDriveStoreListCache = payload;
      },
      /**
       * @callback GetTestDriveStoreListCache
       * @returns {Object|null} 返回缓存的试驾门店列表数据
       */
      getTestDriveStoreListCache() {
        return state.testDriveStoreListCache;
      },
      /**
       * @callback ClearTestDriveStoreListCache
       * 清除试驾门店列表缓存
       */
      clearTestDriveStoreListCache() {
        webStorage.remove(S_TESTDRIVE_STORE_LIST_CACHE);
        state.testDriveStoreListCache = null;
      },
    };
    return {
      ...toRefs(state),
      ...computes,
      ...methods,
    };
  });
}
export default useAppStore;
